// EntryNoticeModal — Mario draft a 24hr entry notice with 1-3 window options.
// Sits inside the Mario Dashboard thread detail panel.
import { useState } from "react";
import { XCircle, Calendar, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

function localToIso(local) {
  // Input from datetime-local is already tz-agnostic ISO-ish "YYYY-MM-DDTHH:MM".
  return local ? new Date(local).toISOString() : "";
}

// Default: two windows starting 24hrs from now.
function defaultWindows() {
  const t = new Date(Date.now() + 24 * 3600 * 1000);
  t.setMinutes(0, 0, 0);
  const isoLocal = (d) => {
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const t2 = new Date(t.getTime() + 2 * 3600 * 1000);
  const t3 = new Date(t.getTime() + 26 * 3600 * 1000);
  const t4 = new Date(t.getTime() + 28 * 3600 * 1000);
  return [
    { start: isoLocal(t), end: isoLocal(t2) },
    { start: isoLocal(t3), end: isoLocal(t4) },
  ];
}

export default function EntryNoticeModal({ threadId, threadSubject, onClose, onCreated }) {
  const [reason, setReason] = useState(threadSubject || "");
  const [techName, setTechName] = useState("");
  const [leaseRef, setLeaseRef] = useState("Standard PA residential lease · entry provision");
  const [advanceHrs] = useState(24);
  const [windows, setWindows] = useState(defaultWindows);
  const [busy, setBusy] = useState(false);

  const addWindow = () => {
    if (windows.length >= 3) return;
    const last = windows[windows.length - 1];
    setWindows([...windows, { start: last.start, end: last.end }]);
  };
  const removeWindow = (i) => setWindows(windows.filter((_, idx) => idx !== i));
  const updateWindow = (i, field, val) => setWindows(windows.map((w, idx) => idx === i ? { ...w, [field]: val } : w));

  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    const valid = windows.filter(w => w.start && w.end);
    if (valid.length === 0) { toast.error("At least one time window required"); return; }
    setBusy(true);
    try {
      await api.post(`/tenant-bridge/threads/${threadId}/entry-notices`, {
        reason: reason.trim(),
        tech_name: techName.trim(),
        lease_reference: leaseRef.trim(),
        advance_hours: advanceHrs,
        windows: valid.map(w => ({ start: localToIso(w.start), end: localToIso(w.end) })),
      });
      toast.success("Entry notice sent — tenant sees it now");
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't send notice")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="entry-notice-modal">
      <div className="w-full max-w-lg bg-white border-4 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="p-3 border-b-2 border-slate-200 flex items-center justify-between bg-amber-50">
          <div className="flex items-center gap-2">
            <Calendar weight="fill" size={20} className="text-amber-600"/>
            <div className="font-black uppercase tracking-widest text-sm">Request 24-hr Entry</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-black"><XCircle size={20}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="p-3 bg-slate-50 border-l-4 border-amber-500 text-xs text-slate-700 leading-relaxed">
            <b>Legal cover:</b> tenant receives windows to pick from, types their legal name,
            we timestamp + IP-log the acceptance and generate a PDF for your file.
            Signature by typed name is binding under the E-SIGN Act + PA UETA.
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Reason for entry</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)}
              rows={2} data-testid="entry-notice-reason"
              placeholder="Repair fridge — mold spots on interior liner"
              className="mt-1 w-full px-3 py-2 border-2 border-[#0f172a] text-sm resize-none"/>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Tech / personnel</label>
              <input value={techName} onChange={e => setTechName(e.target.value)}
                data-testid="entry-notice-tech" placeholder="Kevin (PMR)"
                className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Lease reference</label>
              <input value={leaseRef} onChange={e => setLeaseRef(e.target.value)}
                data-testid="entry-notice-lease" placeholder="Lease §14 · entry"
                className="mt-1 w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Windows offered</label>
              {windows.length < 3 && (
                <button onClick={addWindow} data-testid="entry-notice-add-window"
                  className="text-[10px] font-black uppercase tracking-widest text-cyan-600 hover:text-cyan-800 inline-flex items-center gap-1">
                  <Plus weight="bold" size={12}/> Add window
                </button>
              )}
            </div>
            {windows.map((w, i) => (
              <div key={i} className="flex items-center gap-2 mt-2" data-testid={`entry-notice-window-${i}`}>
                <input type="datetime-local" value={w.start} onChange={e => updateWindow(i, "start", e.target.value)}
                  className="flex-1 h-10 px-2 border-2 border-slate-300 text-xs"/>
                <span className="text-slate-400 text-xs">→</span>
                <input type="datetime-local" value={w.end} onChange={e => updateWindow(i, "end", e.target.value)}
                  className="flex-1 h-10 px-2 border-2 border-slate-300 text-xs"/>
                {windows.length > 1 && (
                  <button onClick={() => removeWindow(i)} className="p-1.5 text-red-500 hover:bg-red-50 border border-slate-200">
                    <Trash size={12}/>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="p-3 border-t-2 border-slate-200 bg-slate-50 flex gap-2 justify-end">
          <button onClick={onClose} className="h-10 px-4 border-2 border-slate-300 font-black uppercase text-xs tracking-widest hover:bg-white">Cancel</button>
          <button onClick={submit} disabled={busy || !reason.trim()} data-testid="entry-notice-submit"
            className="h-10 px-4 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40 inline-flex items-center gap-1">
            <Calendar weight="fill" size={12}/> {busy ? "Sending…" : "Send to tenant"}
          </button>
        </div>
      </div>
    </div>
  );
}
